import { Action, State, StateContext, Store } from '@ngxs/store';
import { PlaylistsStateModel } from '@store/playlists/playlists-state.model';
import { Injectable } from '@angular/core';
import {
  ChangePlaylistCurrentPage,
  ChangePlaylistPerPage, ChangePlaylistViewType,
  ChangeSearchPlaylistParam, CleanPlaylists,
  CleanSearchPlaylistParam,
  CleanSelectedPlaylist,
  DeletePlaylist,
  GetPlaylists,
  GetSelectedPlaylistById,
  SetPlaylistCurrentPage,
  SetPlaylistPerPage, SetPlaylistsDefaultPerPage, SetPlaylistsOrder,
  SetPlaylistViewType,
  SetSelectedPlaylist,
  UpdatePlaylist,
} from '@store/playlists/playlists.actions';
import { itemsPerPage } from '@shared/helpers';
import { Observable } from 'rxjs';
import { PlaylistsService } from '@services/playlists/playlists.service';
import { switchMap, tap } from 'rxjs/operators';
import { IPlaylist } from '@interfaces/playlist';
import { MediaTypeEnum } from '@shared/enums/media-type.enum';
import { CookieService } from 'ngx-cookie';
import { ViewType } from '@shared/enums/view-type.enum';
import { ToggleGlobalLoader, TogglePlaylistLoader } from '@store/loader';
import { SidebarPlaylistsState } from '@store/sidebar-state';


@State<PlaylistsStateModel>({
  name: 'playlistState',
  defaults: {
    playlistsInUse: 0,
    playlists: [],
    searchParam: '',
    currentPage: 1,
    playlistPerPage: itemsPerPage[5],
    selectedPlaylist: null,
    selectedMediaType: MediaTypeEnum.video,
    viewType: ViewType.list,
    order: '',
  }
})
@Injectable()
export class PlaylistsState {
  constructor(private playlistService: PlaylistsService,
              private store: Store,
              private cookie: CookieService,
              private playlistSideBarState: SidebarPlaylistsState) {
  }

  @Action(GetPlaylists, {cancelUncompleted: true})
  getPlaylists(ctx: StateContext<PlaylistsStateModel>): Observable<any> {
    const limit = ctx.getState().playlistPerPage;
    const skip = limit * (ctx.getState().currentPage - 1);
    const search = ctx.getState().searchParam;
    const order = ctx.getState().order ? ctx.getState().order : '-created_at';
    this.store.dispatch(new TogglePlaylistLoader(true));
    return this.playlistService.fetchPlaylists({
      limit,
      skip,
      order,
      search,
      type: 'main'
    }).pipe(
      tap(res => {
        const state = ctx.getState();
        ctx.setState({...state, playlists: res.rows, playlistsInUse: res.total});
        this.store.dispatch(new TogglePlaylistLoader(false));
      })
    );
  }

  @Action(GetSelectedPlaylistById)
  getSelectedPlaylistById(ctx: StateContext<PlaylistsStateModel>, {id}: GetSelectedPlaylistById): Observable<IPlaylist> {
    return this.playlistService.getPlaylistById(id)
      .pipe(
        tap((playlist) => {
          const state = ctx.getState();
          ctx.setState({...state, selectedPlaylist: playlist});
        })
      );
  }

  @Action(UpdatePlaylist)
  updatePlaylist(ctx: StateContext<PlaylistsStateModel>, {id, payload}: UpdatePlaylist): Observable<any> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    return this.playlistService.updatePlaylist(id, payload)
      .pipe(
        tap((playlist) => {
          const state = ctx.getState();
          ctx.patchState({
            playlists: state.playlists.map(pl => {
              if (pl.id === playlist.id) return playlist;
              else return pl;
            }),
            selectedPlaylist: playlist
          });
          this.store.dispatch(new ToggleGlobalLoader(false));
        })
      );
  }

  @Action(SetPlaylistPerPage)
  setPlaylistPerPage(ctx: StateContext<PlaylistsStateModel>, {playlistPerPage}: ChangePlaylistPerPage): PlaylistsStateModel {
    this.cookie.put('playlistsPerPage', playlistPerPage.toString());

    ctx.patchState({currentPage: 1});

    return ctx.patchState({playlistPerPage});
  }

  @Action(ChangePlaylistPerPage)
  changePlaylistPerPage(ctx: StateContext<PlaylistsStateModel>, {playlistPerPage}: ChangePlaylistPerPage): Observable<any> {
    this.setPlaylistPerPage(ctx, {playlistPerPage});
    return ctx.dispatch(new GetPlaylists());
  }

  @Action(SetPlaylistViewType)
  setPlaylistViewType(ctx: StateContext<PlaylistsStateModel>, {viewType}: SetPlaylistViewType): PlaylistsStateModel {
    this.cookie.put('playlistViewType', viewType as ViewType);
    return ctx.patchState({viewType: viewType as ViewType});
  }

  @Action(ChangePlaylistViewType)
  changePlaylistViewType(ctx: StateContext<PlaylistsStateModel>, {viewType}: ChangePlaylistViewType): void {
    this.setPlaylistViewType(ctx, {viewType});
  }

  @Action(SetSelectedPlaylist)
  setSelectedPlaylist(ctx: StateContext<PlaylistsStateModel>, {playlist}: SetSelectedPlaylist): void {
    ctx.setState({...ctx.getState(), selectedPlaylist: playlist});
  }

  @Action(CleanSelectedPlaylist)
  cleanSelectedPlaylist(ctx: StateContext<PlaylistsStateModel>): void {
    ctx.patchState({selectedPlaylist: null});
  }

  @Action(SetPlaylistCurrentPage)
  setPlaylistCurrentPage(ctx: StateContext<PlaylistsStateModel>, {page}: SetPlaylistCurrentPage): void {
    ctx.patchState({currentPage: page});
  }

  @Action(ChangePlaylistCurrentPage)
  changePlaylistCurrentPage(ctx: StateContext<PlaylistsStateModel>, {page}: ChangePlaylistCurrentPage): Observable<any> {
    this.setPlaylistCurrentPage(ctx, {page});
    return ctx.dispatch(new GetPlaylists());
  }

  @Action(CleanSearchPlaylistParam)
  cleanSearchPlaylistParam(ctx: StateContext<PlaylistsStateModel>): PlaylistsStateModel {
    return ctx.patchState({searchParam: ''});
  }

  @Action(ChangeSearchPlaylistParam)
  changeSearchPlaylistParam(ctx: StateContext<PlaylistsStateModel>, {param}: ChangeSearchPlaylistParam): Observable<any> {
    ctx.patchState({searchParam: param, currentPage: 1});
    return ctx.dispatch(new GetPlaylists());
  }

  @Action(DeletePlaylist)
  deletePlaylist(ctx: StateContext<PlaylistsStateModel>, {id}: DeletePlaylist): Observable<void> {
    this.store.dispatch(new ToggleGlobalLoader(true));
    return this.playlistService.deletePlaylist(id)
      .pipe(
        tap(() => {
          const state = ctx.getState();
          ctx.patchState({
            currentPage: this.getCurrentPageAfterDelete(state.currentPage, state.playlistPerPage, state.playlistsInUse),
            playlistsInUse: Math.max(state.playlistsInUse - 1, 0)
          });
          this.playlistSideBarState.refreshAfterDelete();
        }),
        switchMap(() => ctx.dispatch([new GetPlaylists(), new ToggleGlobalLoader(false)]))
      );
  }

  @Action(SetPlaylistsOrder)
  setPlaylistsOrder(ctx: StateContext<PlaylistsStateModel>, {order}: SetPlaylistsOrder): Observable<void> {
    ctx.patchState({order});
    return ctx.dispatch(new GetPlaylists());
  }

  @Action(SetPlaylistsDefaultPerPage)
  setPlaylistsDefaultPerPage(ctx: StateContext<PlaylistsStateModel>): void {
    ctx.patchState({playlistPerPage: itemsPerPage[5]});
  }

  @Action(CleanPlaylists)
  cleanPlaylists(ctx: StateContext<PlaylistsStateModel>): void {
    ctx.patchState({playlists: [], searchParam: '', currentPage: 1});
  }

  private getCurrentPageAfterDelete(currentPage: number, perPage: number, total = 0, deletedCount = 1): number {
    const totalAfterDelete = Math.max(total - deletedCount, 0);
    const lastAvailablePage = Math.max(Math.ceil(totalAfterDelete / perPage), 1);

    return Math.min(currentPage, lastAvailablePage);
  }
}
